import { useEffect, useCallback } from 'react';

import { fetchProjects } from '../actions/projects';

import { createAlert } from '../services/alert-service';
import { logout } from '../globals-service';

const UserListeners = ({ eventEmitter, applyStoreAction, currentUserId }) => {
    const userOnline = useCallback(({ user_id }) => {
        applyStoreAction({
            type: 'USER_UPDATE_ONLINE_STATUS_SOCKET',
            payload: {
                userId: user_id,
                online: true
            }
        });
    }, [applyStoreAction]);

    const userOffline = useCallback(({ user_id }) => {
        applyStoreAction({
            type: 'USER_UPDATE_ONLINE_STATUS_SOCKET',
            payload: {
                userId: user_id,
                online: false
            }
        });
    }, [applyStoreAction]);

    const userUpdated = useCallback(user => {
        applyStoreAction({
            type: 'USER_UPDATE_SOCKET',
            payload: {
                userId: user.id,
                user
            }
        });
    }, [applyStoreAction]);

    const roleChanged = useCallback(({ member_id, new_role, role_id }) => {
        if (member_id === currentUserId && new_role === 'owner') {
            fetchProjects();
            fetchProjects(true);
        }

        applyStoreAction({
            type: 'USER_UPDATE_SOCKET',
            payload: {
                userId: member_id,
                user: {
                    role: new_role,
                    role_id: role_id
                }
            }
        });
    }, [applyStoreAction]);

    const inviteAccept = useCallback(user => {
        applyStoreAction({
            type: 'USER_INVITE_ACCEPT_SOCKET',
            payload: {
                userId: user.id,
                user
            }
        });
    }, [applyStoreAction]);

    const inviteCancel = useCallback(userId => {
        applyStoreAction({
            type: 'USER_INVITE_CANCEL_SOCKET',
            payload: {
                userId
            }
        });
    }, [applyStoreAction]);

    const userEventAdded = useCallback(({ member_id, labels }) => {
        applyStoreAction({
            type: 'USER_LABELS_ADD_SOCKET',
            payload: {
                userId: member_id,
                labels
            }
        });
    }, [applyStoreAction]);

    const userEventRemoved = useCallback(({ member_id, labels }) => {
        applyStoreAction({
            type: 'USER_LABELS_REMOVE_SOCKET',
            payload: {
                userId: member_id,
                labels
            }
        });
    }, [applyStoreAction]);

    const memberUpdated = useCallback(user => {
        if ('available' in user) {
            applyStoreAction({
                type: 'USER_UPDATE_SOCKET',
                payload: {
                    userId: user.id,
                    user: {
                        available: user.available
                    }
                }
            });
        }
    }, [applyStoreAction]);

    const memberRemoved = useCallback(({ member_id, project_id }) => {
        if (project_id) {
            applyStoreAction({
                type: 'PROJECT_USER_REMOVE_SOCKET',
                payload: {
                    userId: member_id,
                    projectId: project_id
                }
            });
        } else if (currentUserId === member_id) {
            createAlert('default', 'You have been removed from the current team.');

            logout();
        } else {
            applyStoreAction({
                type: 'TEAM_USER_REMOVE_SOCKET',
                payload: {
                    userId: member_id
                }
            });
        }
    }, [currentUserId, applyStoreAction]);

    const memberLeft = useCallback(({ member_id }) => {
        applyStoreAction({
            type: 'TEAM_USER_REMOVE_SOCKET',
            payload: {
                userId: member_id
            }
        });
    }, [applyStoreAction]);

    const notificationSettingsChanged = useCallback(({ type, action, enabled, transport }) => {
        applyStoreAction({
            type: 'USER_NOTIFICATION_UPDATE_SOCKET',
            payload: {
                type, action, enabled, transport
            }
        });
    }, [applyStoreAction]);

    const listen = () => {
        eventEmitter.on('user_online', userOnline);
        eventEmitter.on('user_offline', userOffline);
        eventEmitter.on('user_updated', userUpdated);
        eventEmitter.on('role_changed', roleChanged);
        eventEmitter.on('invite_accept', inviteAccept);
        eventEmitter.on('invite_cancel', inviteCancel);
        eventEmitter.on('member_label_added', userEventAdded);
        eventEmitter.on('member_label_removed', userEventRemoved);
        eventEmitter.on('member_updated', memberUpdated);
        eventEmitter.on('member_removed', memberRemoved);
        eventEmitter.on('member_left', memberLeft);
        eventEmitter.on('notification_settings_change', notificationSettingsChanged);

        return () => {
            eventEmitter.off('user_online', userOnline);
            eventEmitter.off('user_offline', userOffline);
            eventEmitter.off('user_updated', userUpdated);
            eventEmitter.off('role_changed', roleChanged);
            eventEmitter.off('invite_accept', inviteAccept);
            eventEmitter.off('invite_cancel', inviteCancel);
            eventEmitter.off('member_label_added', userEventAdded);
            eventEmitter.off('member_label_removed', userEventRemoved);
            eventEmitter.off('member_updated', memberUpdated);
            eventEmitter.off('member_removed', memberRemoved);
            eventEmitter.off('member_left', memberLeft);
            eventEmitter.off('notification_settings_change', notificationSettingsChanged);
        };
    };

    useEffect(listen, [eventEmitter, userOnline, userOffline, userUpdated, roleChanged, inviteAccept, inviteCancel, userEventAdded, userEventRemoved, memberRemoved, memberLeft]);

    return null;
}

export default UserListeners;
